import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TenantSettingApikeyViewModel } from './tenant-setting-apikey-edit-dialog/tenant-setting-apikey-edit-dialog.component';

export type WebhookViewModel = {
  apiKeyName: string;
  webhookUrl: string;
  keyHeaderName: string;
  apiKeyValue: string;
  hasApikey?: boolean;
};

/** Webhookのフォームグループ */
export type WebhookFormGroup = FormGroup<{
  /** API名 */
  apiKeyName: FormControl<string>;
  /** URL */
  webhookUrl: FormControl<string>;
  /** Key Header Name */
  keyHeaderName: FormControl<string>;
  /** API Key Value */
  apiKeyValue: FormControl<string>;
}>;

@Component({
  selector: 'app-tenant-setting-api-key',
  standalone: true,
  imports: [
    MatButtonModule,
    MatGridListModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    CommonModule,
  ],
  templateUrl: './tenant-setting-apikey.component.html',
  styleUrl: './tenant-setting-apikey.component.scss',
})
export class TenantSettingApikeyComponent implements OnChanges {
  /** Webhook */
  @Input({ required: true }) public webhook: WebhookViewModel = {
    apiKeyName: '',
    webhookUrl: '',
    keyHeaderName: '',
    apiKeyValue: '',
    hasApikey: false,
  };

  /** apikey */
  @Input({ required: true }) public apikey: TenantSettingApikeyViewModel = {
    hasApikey: false,
    clientId: '',
    clientSecretValue: '',
  };

  /** Webhook更新を通知 */
  @Output() updated = new EventEmitter<WebhookViewModel>();

  /** Webhook削除を通知 */
  @Output() webhookDeleted = new EventEmitter<WebhookViewModel>();

  /** API Key発行を通知 */
  @Output() created = new EventEmitter<void>();
  /** API Key破棄を通知 */
  @Output() deleted = new EventEmitter<void>();

  /** ツールチップメッセージ */
  public tooltipMessage: string;

  /** 編集フラグ */
  public updating: boolean = false;

  /** フォームグループ */
  public formGroup: WebhookFormGroup;

  /** パスワードの表示非表示 */
  public showPassword: boolean;

  constructor() {
    this.showPassword = false;

    this.tooltipMessage =
      'Webhook の通知を受け取る外部のエンドポイント（及びカスタムヘッダー）を1つ追加できます。\n' +
      'ヘッダーの設定値は、以下のようになります。\n\n' +
      'Content-Type : application/json （固定）\n' +
      'HTTP Method : POST （固定）\n' +
      'カスタムヘッダー名 : 「API Key Header名」の設定値\n' +
      'カスタムヘッダー値 : 「API Key 値」の設定値';
    this.formGroup = new FormGroup({
      apiKeyName: new FormControl('', {
        nonNullable: true,
        // validators: [Validators.required],
      }),
      webhookUrl: new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(/^(https:\/\/)/), Validators.required],
      }),
      keyHeaderName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      apiKeyValue: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['webhook'] !== undefined) {
      this.showPassword = false;

      this.formGroup.get('apiKeyName')?.setValue(this.webhook.apiKeyName);
      this.formGroup.get('webhookUrl')?.setValue(this.webhook.webhookUrl);
      this.formGroup.get('keyHeaderName')?.setValue(this.webhook.keyHeaderName);
      this.formGroup.get('apiKeyValue')?.setValue(this.webhook.apiKeyValue);
      this.apikey.hasApikey = this.webhook.hasApikey ?? false;
    }
  }

  public updatedWebhook(): void {
    this.updating = !this.updating;

    const webhook: WebhookViewModel = {
      apiKeyName: '',
      // apiKeyName: this.formGroup.get('apiKeyName')?.value ?? '',
      webhookUrl: this.formGroup.get('webhookUrl')?.value ?? '',
      keyHeaderName: this.formGroup.get('keyHeaderName')?.value ?? '',
      apiKeyValue: this.formGroup.get('apiKeyValue')?.value ?? '',
    };
    this.updated.emit(webhook);
  }

  public deletedWebhook(): void {
    const webhook: WebhookViewModel = {
      apiKeyName: '',
      // apiKeyName: this.formGroup.get('apiKeyName')?.value ?? '',
      webhookUrl: this.formGroup.get('webhookUrl')?.value ?? '',
      keyHeaderName: this.formGroup.get('keyHeaderName')?.value ?? '',
      apiKeyValue: this.formGroup.get('apiKeyValue')?.value ?? '',
    };
    this.webhookDeleted.emit(webhook);
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public cancelEditing(): void {
    this.formGroup.reset();

    this.updating = false;
    this.formGroup.get('apiKeyName')?.setValue(this.webhook.apiKeyName);
    this.formGroup.get('webhookUrl')?.setValue(this.webhook.webhookUrl);
    this.formGroup.get('keyHeaderName')?.setValue(this.webhook.keyHeaderName);
    this.formGroup.get('apiKeyValue')?.setValue(this.webhook.apiKeyValue);
  }
}
